import { lazy, Suspense, useState } from 'react';
import ConsoleTabs, { panelProps } from '../../components/ConsoleTabs.jsx';
import PageState from '../../components/PageState.jsx';
import { useTabParam } from '../../lib/use-tab-param.js';
import { complianceAllowedForTier } from '../../lib/compliance-tier.js';
import { typesAvailable, typesUnavailable } from '../../lib/report-types.js';

// Posture reports with benchmark readiness (ENTRAID-307). The section shell: tabs by role, tier gating, the upsell
// card. The tabs load their own data from the API; nothing here knows a framework or a control (the catalogs stay on
// the server). Display only: the API enforces role and tier on every route.

const TAB_COMPONENTS = Object.freeze({
  reports: lazy(() => import('./ReportsTab.jsx')),
  controls: lazy(() => import('./ControlsTab.jsx')),
  attestations: lazy(() => import('./AttestationsTab.jsx')),
  schedule: lazy(() => import('./ScheduleTab.jsx')),
});

const ALL_TABS = Object.freeze([
  { id: 'reports', label: 'Reports', roles: ['admin', 'auditor', 'operator'] },
  { id: 'controls', label: 'Controls', roles: ['admin', 'auditor'] },
  { id: 'attestations', label: 'Attestations', roles: ['admin', 'auditor'] },
  { id: 'schedule', label: 'Schedule', roles: ['admin'] },
]);
const TAB_IDS = ALL_TABS.map((t) => t.id);
const PREFIX = 'reports';

/**
 * @param {object} props
 * @param {string} props.customerId
 * @param {'admin'|'auditor'|'operator'} props.role
 * @param {string|null} [props.tier] the customer's tier name; null or undefined with tierKnown false when the
 *   account cannot read the customer record (an auditor), in which case the API's own tier answer is shown instead.
 * @param {boolean} [props.tierKnown]
 * @param {string} [props.marketplaceListingUrl] kept for callers; the plan upsell now lives in the generate dialog
 * @param {object|null} [props.customer] the customer record (admins and operators only); the Schedule tab reads the report stack readiness from it
 * @param {string} [props.userEmail] the signed-in user's email (read-only text in the attestation dialog)
 */
export default function ComplianceSection({ customerId, role, tier, tierKnown = true, marketplaceListingUrl = '', userEmail = '', customer = null }) {
  const [, setReportCount] = useState(0);
  const [tab, setTab] = useTabParam(TAB_IDS);
  // Security Posture is in every tier (decision D2b); Compliance Readiness needs Professional or Enterprise. The tier decides
  // per type: a customer below it still generates and reads Security Posture reports, keeps read access to the Compliance
  // Readiness reports it already has, and sees the next tier in the generate dialog (Compliance Readiness shown disabled
  // with the reason). Only the Reports tab applies below the tier: Controls, Attestations and the Schedule are Compliance Readiness.
  const below = tierKnown && !complianceAllowedForTier(tier);
  // Below the tier an admin still has the Schedule tab: it holds the Security Posture schedule (every tier) and the one
  // shared retention; its Compliance Readiness block shows the tier note.
  const tabs = below ? ALL_TABS.filter((t) => t.id === 'reports' || (t.id === 'schedule' && t.roles.includes(role))) : ALL_TABS.filter((t) => t.roles.includes(role));
  if (tabs.length === 0) return null;
  const active = tabs.some((t) => t.id === tab) ? tab : 'reports';

  const Body = (
    <Suspense fallback={<PageState state="loading" header={false} cards={0} label="Loading reports" />}>
      <TabBody id={active} types={tierKnown ? typesAvailable({ tier }) : undefined} unavailable={tierKnown ? typesUnavailable({ tier }) : undefined} customerId={customerId} role={role} complianceLocked={below} customer={customer} userEmail={userEmail} marketplaceListingUrl={marketplaceListingUrl} onReportCount={setReportCount} />
    </Suspense>
  );

  return (
    // The page title is the section's PageHeader h1 (ENTRAID-347 redesign); the section keeps its accessible name.
    <section aria-label="Reports" className="space-y-4">
      {role === 'operator' && (
        <p className="text-sm text-ipc-muted">Operator view: report health only. Report content is available to the customer and its auditors.</p>
      )}
      {tabs.length > 1 ? (
        <>
          <ConsoleTabs label="Reports sections" items={tabs} value={active} onChange={setTab} idPrefix={PREFIX} />
          <div {...panelProps(PREFIX, active)}>{Body}</div>
        </>
      ) : (
        <div>{Body}</div>
      )}
    </section>
  );
}

function TabBody({ id, ...props }) {
  const Tab = TAB_COMPONENTS[id];
  return <Tab {...props} />;
}
